// Which agents can run against a given upload.
//
// Mirrors the server's agentAppliesToUpload() (api/src/agents/customAgentStore.js)
// so the "Run agent" picker offers exactly the agents capable of handling this
// document - offering one that can't just produces a failed run the user has
// to interpret.
//
// One deliberate difference from the auto-run path: autoRun is NOT consulted
// here. An agent with autoRun off is precisely one the user asked to run by
// hand, so it must stay in this picker; it is only excluded from firing by
// itself on upload. `enabled: false`, by contrast, means the agent is off
// entirely and is excluded from both.
//
// Kept deliberately identical to the backend, including the alias table and
// the "no categories configured -> no match" rule. That last point also
// filters out the built-in system agents (Photo Analysis, Drawing
// Intelligence, Chat with your data), which carry no categories and are not
// resolvable by the requeue endpoint anyway - it looks agents up in the custom
// agent store, so queueing one returns agent_not_found.

const UPLOAD_CATEGORIES = ['Drawings', 'Specifications', 'Instructions', 'Contract', 'Images', 'Reports'];

const CATEGORY_ALIASES = {
  drawing: 'Drawings',
  drawings: 'Drawings',
  specification: 'Specifications',
  specifications: 'Specifications',
  instruction: 'Instructions',
  instructions: 'Instructions',
  contract: 'Contract',
  contracts: 'Contract',
  image: 'Images',
  images: 'Images',
  photo: 'Images',
  photos: 'Images',
  report: 'Reports',
  reports: 'Reports',
};

function str(value) {
  return String(value ?? '').trim();
}

export function normaliseCategories(value) {
  const arr = Array.isArray(value) ? value : String(value || '').split(',');
  const out = [];
  for (const item of arr) {
    const raw = str(item);
    if (!raw) continue;
    const key = raw.toLowerCase();
    // Canonicalise known synonyms/built-ins; otherwise keep the value as-is so
    // custom upload types defined in Settings still match.
    const category = CATEGORY_ALIASES[key] || UPLOAD_CATEGORIES.find((x) => x.toLowerCase() === key) || raw;
    if (category && !out.some((x) => x.toLowerCase() === category.toLowerCase())) out.push(category);
  }
  return out;
}

function fileExtensionOf(value = '') {
  const match = /\.([a-z0-9]+)$/i.exec(str(value));
  return match ? match[1].toLowerCase() : '';
}

function normaliseFileTypeList(value) {
  const arr = Array.isArray(value) ? value : String(value || '').split(',');
  return arr.map((v) => str(v).toLowerCase().replace(/^\./, '')).filter(Boolean);
}

function agentCategoryMatches(agent = {}, doc = {}) {
  const docCategories = normaliseCategories(doc.uploadType || doc.documentType || doc.type);
  const agentCategories = normaliseCategories(agent.appliesTo || agent.categories || agent.uploadCategories).map((x) => x.toLowerCase());
  if (!agentCategories.length) return false;
  return docCategories.some((category) => agentCategories.includes(category.toLowerCase()));
}

// An agent with no supportedFileTypes is unrestricted by format; when its
// creator did configure formats (e.g. "xlsx"), a Documents-category agent
// scoped to Excel must not be offered for a PDF filed under that category.
function agentFileTypesMatch(agent = {}, doc = {}) {
  const allowed = normaliseFileTypeList(agent.supportedFileTypes || agent.supports);
  if (!allowed.length) return true;

  const ext = fileExtensionOf(doc.fileName || doc.blobName || doc.name);
  if (ext && allowed.includes(ext)) return true;

  const contentType = str(doc.contentType).toLowerCase();
  return !!contentType && allowed.some((type) => contentType.includes(type));
}

// The built-in agents carry no categories, so the custom-agent rule above
// always rejects them - yet they are exactly the agents that produced the
// document's original analysis, and the obvious thing to want to re-run.
// They also can't go through the requeue endpoint, which resolves agents in
// the custom agent store; they re-run through /api/mobile/run-ai instead.
//
// Mirrors systemAgentPlan() in api/src/agents/agentUploadOrchestrator.js:
// Drawings + PDF -> Drawing Intelligence, Images + image -> Photo Analysis.
const SYSTEM_AGENT_RULES = {
  drawingIntelligence: { category: 'Drawings', match: isPdfDoc },
  photoAnalysis: { category: 'Images', match: isImageDoc },
};

function isPdfDoc(doc = {}) {
  const type = str(doc.contentType).toLowerCase();
  const name = str(doc.fileName || doc.blobName).toLowerCase();
  return type.includes('pdf') || name.endsWith('.pdf');
}

function isImageDoc(doc = {}) {
  const type = str(doc.contentType).toLowerCase();
  const name = str(doc.fileName || doc.blobName).toLowerCase();
  return type.startsWith('image/') || /\.(png|jpe?g|webp|gif|bmp|tiff?|heic|heif)$/i.test(name);
}

export function isSystemAgent(agent = {}) {
  return Object.prototype.hasOwnProperty.call(SYSTEM_AGENT_RULES, agent.id);
}

function systemAgentApplies(agent = {}, doc = {}) {
  const rule = SYSTEM_AGENT_RULES[agent.id];
  if (!rule) return false;
  const docCategories = normaliseCategories(doc.uploadType || doc.documentType || doc.type);
  if (!docCategories.some((c) => c.toLowerCase() === rule.category.toLowerCase())) return false;
  return rule.match(doc);
}

export function agentAppliesToDocument(agent = {}, doc = {}) {
  if (isSystemAgent(agent)) return systemAgentApplies(agent, doc);
  if (agent.enabled === false) return false;
  return agentCategoryMatches(agent, doc) && agentFileTypesMatch(agent, doc);
}

export function agentsForDocument(agents = [], doc = {}) {
  return (Array.isArray(agents) ? agents : []).filter((agent) => agentAppliesToDocument(agent, doc));
}
